import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IonList, IonRefresher, IonRefresherContent, IonText } from '@ionic/react';
import cbbTeams from '../../data/Sports/CBB.json';
import mlbTeams from '../../data/Sports/MLB.json';
import nbaTeams from '../../data/Sports/NBA.json';
import cfbTeams from '../../data/Sports/NCAAF.json';
import nflTeams from '../../data/Sports/NFL.json';
import nhlTeams from '../../data/Sports/NHL.json';
import nwslTeams from '../../data/Sports/NWSL.json';
import soccerTeams from '../../data/Sports/SOCCER.json';
import specialsTeam from '../../data/Sports/SPECIALS.json';
import wnbaTeams from '../../data/Sports/WNBA.json';
import { AppContext } from '../../global/AppContext';
import MatchmakingContext from '../../global/MatchmakingContext';
// eslint-disable-next-line no-unused-vars
import { getOpenLiveBets, acceptOpenLiveBet, TAKEOUT_TYPES, acceptFight } from '../../global/request/bet';
import useTeamsUIConfig from '../../global/useTeamsUIConfig';
import { AuthContext } from '../authentication/AuthContext';
import TakerFightProcess from '../fight/takerFightProcess/TakerFightProcess';
import MatchmakingFightCard from '../fightCardV2.1.0/MatchmakingFightCard';
import { MatchmakingFightCardLoader } from '../skeletalLoaders/SkeletalLoaders';
import '../../styles/home/MatchmakingFeed.scss';

// eslint-disable-next-line react/function-component-definition
const MatchmakingFeed = ({ restaurant }) => {
  const [userBets, setUserBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const appCtx = useContext(AppContext);
  const authCtx = useContext(AuthContext);
  const { state, dispatch } = useContext(MatchmakingContext);
  const [activeBet, setActiveBet] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [modalAnimationClass, setModalAnimationClass] = useState('modal-slide-in');
  // eslint-disable-next-line no-unused-vars
  const [reload, specialsTeamsConfig, loading, errors] = useTeamsUIConfig();
  const loaderKeys = useRef(Array.from({ length: 8 }).map(() => uuidv4()));
  const takerProcessRef = useRef(null);
  const allTeams = useMemo(
    () => [
      ...nbaTeams,
      ...nflTeams,
      ...cfbTeams,
      ...nhlTeams,
      ...mlbTeams,
      ...soccerTeams,
      ...cbbTeams,
      ...wnbaTeams,
      ...nwslTeams,
      ...specialsTeam,
    ],
    [],
  );

  const optionalTakerVenue = useMemo(() => {
    if (activeBet?.takeoutType === TAKEOUT_TYPES.DELIVERY && appCtx.restaurant) {
      return {
        id: appCtx.restaurant.id,
        name: appCtx.restaurant.name,
        takeoutType: appCtx.isDelivery ? TAKEOUT_TYPES.DELIVERY : TAKEOUT_TYPES.IN_PERSON,
      };
    }
    return null;
  }, [activeBet?.takeoutType, appCtx.restaurant, appCtx.isDelivery]);

  const findTeamAcronym = useCallback(
    teamName => {
      let foundTeam = allTeams.find(team => team.name === teamName);
      if (!foundTeam) {
        foundTeam = specialsTeamsConfig.find(team => team.name === teamName);
      }
      if (!foundTeam) {
        foundTeam = specialsTeamsConfig.find(team => team.acronym === teamName);
      }
      if (foundTeam) {
        return foundTeam.acronym;
      }
      if (teamName.split(' ')[1]) {
        return teamName.split(' ')[1];
      }
      return teamName;
    },
    [allTeams, specialsTeamsConfig],
  );

  const findTeamColor = useCallback(
    (teamName, sport) => {
      const DEFAULT_COLOR = '#0000ff';
      let foundTeam = allTeams.find(team => team.name === teamName && team.sport === sport);
      if (!foundTeam) {
        foundTeam = specialsTeamsConfig.find(team => team.name === teamName);
      }
      if (!foundTeam) {
        const teamNameAcr = findTeamAcronym(teamName);
        foundTeam = allTeams.find(team => team.acronym === teamNameAcr && team.sport === sport);
        if (!foundTeam) {
          foundTeam = specialsTeamsConfig.find(team => team.acronym === teamNameAcr);
        }
      }
      const color = foundTeam ? foundTeam.color : DEFAULT_COLOR;
      return color;
    },
    [allTeams, specialsTeamsConfig, findTeamAcronym],
  );

  const shortenPhrase = useCallback((phrase, teamName) => {
    return phrase.replace(`${teamName} `, '');
  }, []);

  const getMatchmakingBets = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getOpenLiveBets(authCtx.tokens.idToken);
      const filteredData = data.filter(bet => {
        if (restaurant && restaurant.name.toLowerCase() === 'delivery') {
          return bet.restaurantName.toLowerCase() === 'delivery' && bet.accepted !== 3;
        }
        return bet.restaurantName.toLowerCase() !== 'delivery' && bet.accepted !== 3;
      });
      const updatedBets = filteredData.map(bet => ({
        ...bet,
        makerOutcomeTeamColor: findTeamColor(bet.makerOutcomeTeam, bet.sport),
        takerOutcomeTeamColor: findTeamColor(bet.takerOutcomeTeam, bet.sport),
        makerTeamAcronym: findTeamAcronym(bet.makerOutcomeTeam),
        takerTeamAcronym: findTeamAcronym(bet.takerOutcomeTeam),
        outcomePhrase: bet.makerOutcomePhrase,
      }));
      dispatch({ type: 'SET_USER_BETS', payload: updatedBets });
      setUserBets(updatedBets);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  }, [authCtx.tokens.idToken, restaurant, dispatch, setUserBets, setIsLoading, findTeamAcronym, findTeamColor]);

  const openModalWithBetData = bet => {
    if (bet.accepted === 3) {
      getMatchmakingBets();
      return;
    }
    if (bet.sport === 'Specials') {
      bet.makerTeamAcronym = findTeamAcronym(bet.makerOutcomeTeam);
      bet.takerTeamAcronym = findTeamAcronym(bet.takerOutcomeTeam);
      bet.makerOutcomeTeamColor = findTeamColor(bet.makerOutcomeTeam);
      bet.takerOutcomeTeamColor = findTeamColor(bet.takerOutcomeTeam);
    }
    setActiveBet(bet);
    takerProcessRef.current?.present();
    setModalAnimationClass('modal-slide-in');
  };

  useEffect(() => {
    if (appCtx.restaurant && userBets) {
      const filtered = appCtx.isDelivery
        ? userBets?.filter(bet => bet.takeoutType === TAKEOUT_TYPES.DELIVERY)
        : userBets?.filter(
            bet => bet.restaurantName === appCtx.restaurant.name && bet.takeoutType === TAKEOUT_TYPES.IN_PERSON,
          );
      setFilteredBets(filtered);
    } else {
      setFilteredBets([]);
    }
  }, [appCtx.restaurant, appCtx.isDelivery, userBets]);

  useEffect(() => {
    getMatchmakingBets();
  }, [restaurant, state.triggerGetLiveBets, getMatchmakingBets]);

  const handleReloadBets = async event => {
    await getMatchmakingBets();
    event.detail.complete();
  };

  useEffect(() => {
    setUserBets(state.userBets);
  }, [state.userBets]);

  const handleAcceptFight = async (betId, payment, address, items, deliveryInstructions, deliveryLat, deliveryLng) => {
    try {
      await acceptOpenLiveBet(
        authCtx.tokens.idToken,
        betId,
        payment,
        address,
        items,
        deliveryInstructions,
        deliveryLat,
        deliveryLng,
      );
      getMatchmakingBets();
    } catch (error) {
      console.error('Error in accepting bet:', error);
    }
  };

  let content;

  if (isLoading) {
    content = (
      <IonList>
        {Array.from({ length: 8 }).map((_, index) => (
          <MatchmakingFightCardLoader key={loaderKeys.current[index]} />
        ))}
      </IonList>
    );
  } else if (filteredBets && filteredBets.length > 0) {
    content = filteredBets.map(bet => (
      <MatchmakingFightCard
        key={bet.id}
        fight={{
          sportLeague: bet.sport,
          makerName: bet.makerName,
          makerTeam: bet.makerOutcomeTeam,
          makerTeamAcronym: bet.makerTeamAcronym,
          makerTeamColor: bet.makerOutcomeTeamColor,
          makerOutcomePhrase: shortenPhrase(bet.makerOutcomePhrase, bet.makerOutcomeTeam),
          takerName: bet.takerName,
          takerTeam: bet.takerOutcomeTeam,
          takerTeamAcronym: bet.takerTeamAcronym,
          takerTeamColor: bet.takerOutcomeTeamColor,
          takerOutcomePhrase: shortenPhrase(bet.takerOutcomePhrase, bet.takerOutcomeTeam),
          restaurantName: bet.restaurantName,
          makerItems: bet.restaurantItems,
          startTime: bet.startTime,
        }}
        onClickCard={() => openModalWithBetData(bet)}
      />
    ));
  } else {
    content = (
      <div className='no-fights-text-wrap'>
        <IonText className='no-fights-text'>No public FoodFights are live.</IonText>
        <IonText className='no-fights-text'>
          Click <b> Fight button </b> to start!!!
        </IonText>
      </div>
    );
  }

  return (
    <div>
      {content}

      <TakerFightProcess
        ref={takerProcessRef}
        fightData={activeBet}
        onAcceptFight={handleAcceptFight}
        optionalTakerVenue={optionalTakerVenue}
      />
      <IonRefresher slot='fixed' onIonRefresh={handleReloadBets}>
        <IonRefresherContent />
      </IonRefresher>
    </div>
  );
};

export default MatchmakingFeed;
